import React from 'react';
import { Modal, Button, Card, ContentCard, SplitContainer, RadioLabel, HeaderCard, TitleCard, SelectLabel, Section } from '@weezevent/nacre';
import SeatInventory from '../../../../../../models/seatInventory';
import { useForm } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import { Inventory } from '../../../../../../models'; // eslint-disable-line
import Contingent from '../../../../../../models/contingentModel';
import LoadingCard from '../../../../../../components/loadingCard';
import Map from './SeatMap';
import css from './seatmap.module.css';

function SeatMapModal({ toggle, onToggle, zone, organization, chartId, categories, contingents, loadingzones, synczones }) {
    const { t } = useTranslation();
    const rule = /^[A-Za-z0-9-_]*$/;

    const [seatForm, setSeatForm, setSeatFormObject] = useForm({
        type: 'categories',
        inventory: 'default',
        selectedSeats: [],
        selectedCategory: 'default',
        selectedContingent: 'default',
        selectedInventories: [],
        propagationMode: 0,
        access: null,
        row: null,
        label: null,
        seat: null
    });

    const [inventories, loadingInventories] = Inventory.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            query: {
                exclude_sessions: true
            },
            launch: toggle
        },
        [organization, chartId, toggle]
    );

    const [seats, loadingSeats, sync] = SeatInventory.useApiModel(
        {
            organizationId: organization.id,
            zoneId: zone.uuid,
            chartId,
            inventoryId: seatForm.inventory?.uuid,
            launch: seatForm.inventory !== 'default',
            cache: false
        },
        [organization, seatForm.inventory, zone, chartId]
    );

    const inventoriesOptions = React.useMemo(() => {
        let list = [{ text: t('ticket.charts.zone.default_no_inventory'), value: 'default' }];
        inventories
            ?.filter(iv => iv.active && !iv.virtual)
            .map(iv =>
                list.push({
                    text: iv.name,
                    value: iv.uuid
                })
            );
        return list;
    }, [inventories]);

    const handleInventorySwitch = React.useCallback(
        inv => {
            const inventory = inventories.find(iv => iv.uuid === inv);
            setSeatForm('inventory', inventory || 'default');
        },
        [setSeatForm, inventories]
    );

    const inputValidity = React.useCallback(
        entry => {
            if (new RegExp(rule).test(entry)) {
                return true;
            }
            return false;
        },
        [rule]
    );

    const inputError = React.useCallback(
        item => {
            if (!item || new RegExp(rule).test(item)) {
                return null;
            }
            return t('ticket.charts.zone.input_error');
        },
        [rule]
    );

    return (
        <Modal className={css['swap-seat-modal']} size="fullscreen" open={toggle} allowExternalClosing={false} onClose={onToggle}>
            <SplitContainer
                className={css['modal__split-container']}
                additional={
                    <SidePanel
                        form={seatForm}
                        zone={zone}
                        categories={categories}
                        contingents={contingents}
                        onToggle={onToggle}
                        seats={seats}
                        loadingSeats={loadingSeats}
                        loadingzones={loadingzones}
                    />
                }
            >
                <Card className={css['modal-card-container']}>
                    <LoadingCard loading={loadingInventories}>
                        <HeaderCard separator={false}>
                            <TitleCard title={t('ticket.charts.zone_editor.title')} />
                            <Selector items={['categories', 'contingents']} form={seatForm} setForm={setSeatForm} />
                            <SelectLabel
                                label={t('ticket.common.inventories')}
                                placeholder={t('ticket.rates.inventories-placeholder')}
                                value={seatForm.inventory?.uuid || 'default'}
                                options={inventoriesOptions}
                                onChange={handleInventorySwitch}
                                disabled={!inventories?.length}
                                useNew
                            />
                        </HeaderCard>
                        <Map
                            zone={zone}
                            sync={sync}
                            seats={seats}
                            form={seatForm}
                            chartId={chartId}
                            synczones={synczones}
                            setForm={setSeatForm}
                            inputError={inputError}
                            categories={categories}
                            contingents={contingents}
                            inventories={inventories}
                            loadingzones={loadingzones}
                            loadingSeats={loadingSeats}
                            organization={organization}
                            inputValidity={inputValidity}
                            setFormObject={setSeatFormObject}
                            inventoriesOptions={inventoriesOptions}
                        />
                    </LoadingCard>
                </Card>
            </SplitContainer>
        </Modal>
    );
}

export default SeatMapModal;

function SidePanel({ form, zone, onToggle, categories, contingents, seats, loadingSeats, loadingzones }) {
    const { t } = useTranslation();

    const items = React.useMemo(() => {
        return form.type === 'contingents' ? contingents : categories;
    }, [form, contingents, categories]);

    return (
        <Card className={css['swap-seat-list']}>
            <div className={css['content-container']}>
                <HeaderCard separator={false}>
                    <TitleCard title={t(`ticket.charts.chartlist.${form.type}`)} />
                </HeaderCard>
                <ContentCard className={css['content-categories-list']}>
                    <LoadingCard loading={form.inventory?.uuid ? loadingSeats || loadingzones : loadingzones}>
                        {items.map(item => (
                            <SeatedContent key={item.uuid} form={form} zone={zone} instance={item} seats={seats} />
                        ))}
                    </LoadingCard>
                </ContentCard>
            </div>
            <ContentCard className={css['footer']}>
                <Button fluid primary label={t(`ticket.common.action.finish`)} onClick={onToggle} />
            </ContentCard>
        </Card>
    );
}

function SeatedContent({ form, zone, instance, seats }) {
    const [toggle, setToggle] = React.useState(false);
    const zoneSeats = React.useMemo(() => {
        if (!form.inventory?.uuid) {
            return zone.seats;
        }
        return seats;
    }, [form, zone, seats]);

    const filteredSeats = React.useMemo(() => {
        const item = instance instanceof Contingent ? 'contingent' : 'category';
        return zoneSeats?.filter(s => s[item] === instance.uuid).length;
    }, [zoneSeats, instance]);

    const itemPercentage = React.useMemo(() => {
        return `${(filteredSeats / zoneSeats?.length) * 100}%`;
    }, [zoneSeats, filteredSeats]);

    return (
        <div onClick={() => setToggle(!toggle)}>
            <Section className={css['category-list']}>
                <div className={css['category-list-title']}>
                    <div className={css['category-list-dot']} style={{ backgroundColor: instance.color }} /> {instance.name}
                </div>
                {(filteredSeats >= 1 || toggle) && <SeatedStats zoneSeats={zoneSeats} item={instance} filteredSeats={filteredSeats} itemPercentage={itemPercentage} />}
            </Section>
        </div>
    );
}

const SeatedStats = React.memo(({ zoneSeats, item, filteredSeats, itemPercentage }) => {
    const { t } = useTranslation();
    return (
        <>
            <div className={css['category-stats']}>
                <span className={css['category-stats-seats']}>{`${filteredSeats} / `}</span>
                <span>{t('ticket.charts.seats.count.seat', { count: zoneSeats?.length })}</span>
            </div>
            <div>
                <div className={css['category-percentage']} style={{ backgroundColor: item.color, width: itemPercentage }} />
                <div className={css['category-percentage-block']} />
            </div>
        </>
    );
});

function Selector({ form, setForm, items }) {
    const { t } = useTranslation();

    return items.map(i => {
        return (
            <RadioLabel
                key={i}
                label={<div className={css['radio-type-selector']}>{t(`ticket.charts.chartlist.${i}`)}</div>}
                value={i}
                checked={form.type === i}
                inline
                bordered
                onChange={({ target: { value } }) => {
                    setForm('type', value);
                }}
            />
        );
    });
}
